<!-- 吐司 -->
<template>
  <transition name="toast-slide">
    <div
      v-if="visible"
      class="toast"
      :class="`toast--${type}`"
      :style="{ top: `${offset}px` }"
      role="alert"
      aria-live="polite"
    >
      <component :is="typeIcon" class="toast-icon" />
      <span class="toast-text">{{ message }}</span>
      <div
        v-if="duration > 0"
        class="toast-countdown"
        :style="{ animationDuration: `${duration}ms` }"
      ></div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { InfoFilled, SuccessFilled, WarningFilled, CircleCloseFilled } from '@element-plus/icons-vue'

type ToastType = 'info' | 'success' | 'warning' | 'error'

const props = withDefaults(
  defineProps<{
    message?: string
    type?: ToastType
    duration?: number
    /** 距顶部偏移，由 toast 管理器统一计算实现堆叠 */
    offset?: number
  }>(),
  {
    message: '',
    type: 'info',
    duration: 3000,
    offset: 20
  }
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'destroy'): void
}>()

const typeIcons: Record<ToastType, any> = {
  info: InfoFilled,
  success: SuccessFilled,
  warning: WarningFilled,
  error: CircleCloseFilled
}
const typeIcon = computed(() => typeIcons[props.type])

const visible = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null

function startTimer(duration: number) {
  if (timer) clearTimeout(timer)
  if (duration > 0) {
    timer = setTimeout(handleClose, duration)
  }
}

// 显示吐司并开始自动关闭计时
onMounted(() => {
  visible.value = true
  startTimer(props.duration)
})

// 监听 duration 变化重置计时
watch(
  () => props.duration,
  (newDuration) => {
    if (visible.value) startTimer(newDuration)
  }
)

function handleClose() {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  visible.value = false
  emit('close')

  // 等待离场动画完成后触发销毁
  setTimeout(() => {
    emit('destroy')
  }, 300)
}
</script>

<style scoped>
/* 各类型配色（浅色底 + 同色系边框/图标，与 Element Plus 主题一致） */
.toast--info {
  --toast-color: #2f74fa;
  --toast-bg: #ecf3ff;
  --toast-border: #d6e4ff;
}
.toast--success {
  --toast-color: #67c23a;
  --toast-bg: #f0f9eb;
  --toast-border: #e1f3d8;
}
.toast--warning {
  --toast-color: #e6a23c;
  --toast-bg: #fdf6ec;
  --toast-border: #faecd8;
}
.toast--error {
  --toast-color: #f56c6c;
  --toast-bg: #fef0f0;
  --toast-border: #fde2e2;
}

.toast {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 300px;
  max-width: 480px;
  padding: 11px 18px;
  box-sizing: border-box;
  background: var(--toast-bg);
  border: 1px solid var(--toast-border);
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: top 0.3s ease;
}

.toast-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  color: var(--toast-color);
}

.toast-text {
  font-size: 14px;
  color: #303133;
  line-height: 1.5;
  word-break: break-word;
}

/* 底部倒计时线：随剩余展示时间收缩 */
.toast-countdown {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 2px;
  background: var(--toast-color);
  opacity: 0.35;
  transform-origin: left;
  animation: toast-countdown linear forwards;
}

@keyframes toast-countdown {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

/* 进出场动画 */
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.toast-slide-enter-from,
.toast-slide-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-16px) scale(0.96);
}
</style>
